import { createFileRoute, Link, Outlet, redirect } from '@tanstack/react-router';
import {
  ActivityIcon,
  ArrowLeftIcon,
  BuildingIcon,
  KeyRoundIcon,
  UserPlusIcon,
  PackageIcon,
  ScrollTextIcon,
  ServerCogIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { ThemeToggle } from '@/components/theme-toggle';
import { getAuthState } from '@/lib/auth-store';
import { cn } from '@/lib/utils';

const ADMIN_NAV = [
  { label: 'Analytics', to: '/admin', icon: ActivityIcon, exact: true },
  { label: 'Organisations', to: '/admin/customers', icon: BuildingIcon, exact: true },
  { label: 'New client', to: '/admin/clients/new', icon: UserPlusIcon, exact: false },
  { label: 'Licences', to: '/admin/licences', icon: KeyRoundIcon, exact: false },
  { label: 'Catalogue', to: '/admin/catalogue', icon: PackageIcon, exact: false },
  { label: 'Operations', to: '/admin/operations', icon: ServerCogIcon, exact: false },
  { label: 'Audit log', to: '/admin/audit', icon: ScrollTextIcon, exact: false },
] as const;

/**
 * Platform admin runs in the same app but wears a different face: teal accent,
 * dark chrome, and a permanent "Platform" badge so nobody confuses it with a
 * customer workspace.
 */
function AdminLayout() {
  const { user } = getAuthState();

  return (
    <div className="flex min-h-dvh flex-col bg-surface">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-hairline bg-admin-bar px-5 text-admin-ink">
        <span className="flex size-6 items-center justify-center rounded-sm bg-admin-accent text-micro font-bold text-admin-bar">
          SF
        </span>
        <span className="text-data font-semibold tracking-tight">SiteFlow</span>
        <Badge variant="platform" className="border-admin-accent/50 bg-transparent text-admin-accent">
          Platform
        </Badge>

        <nav aria-label="Platform admin" className="ml-4 flex items-center gap-1">
          {ADMIN_NAV.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              activeOptions={{ exact: item.exact }}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-data',
                'transition-colors hover:bg-white/10',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-admin-accent',
              )}
              activeProps={{ className: 'bg-white/10 text-admin-accent font-medium' }}
            >
              <item.icon className="size-3.5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <span className="hidden font-mono text-micro opacity-70 sm:inline">
            {user?.platformRole}
          </span>
          <ThemeToggle />
          <Link
            to="/app"
            className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-data transition-colors hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-admin-accent"
          >
            <ArrowLeftIcon className="size-3.5" />
            Back to dashboard
          </Link>
        </div>
      </header>

      <main className="flex-1 px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
}

export const Route = createFileRoute('/admin')({
  beforeLoad: ({ location }) => {
    const { status, user } = getAuthState();

    if (status !== 'authenticated') {
      throw redirect({
        to: '/login',
        search: { redirect: `${location.pathname}${location.searchStr}` },
      });
    }

    if (!user?.platformRole) {
      toast.error('Platform admin is restricted to SiteFlow staff.');
      throw redirect({ to: '/app' });
    }
  },
  component: AdminLayout,
});
